"use client";

import React from "react";
import { SectionMessage } from "@hubble/web";
import get from "lodash/get";

import { Body } from "app/_components/components/Text/Body";
import { buildContentfulImageUrl } from "components/common/utils/contentful-utils";
import TokenCardSlider from "../TokenCardSlider";
import SmallCard from "../SmallCard/SmallCard";
import CoinPosts from "../CoinPosts/CoinPosts";
import CoinResources from "../CoinResources/CoinResources";
import FaqAccordion from "../FaqAccordion/FaqAccordion";
import RecentCoin from "../RecentCoin/RecentCoin";
import CoinPriceChart from "../CoinPriceChart";
import AssetRiskSummary from "../AssetRiskSummary/AssetRiskSummary";
import { getCoinType } from "utils/coins";
import UkRiskSummary from "../UkRiskSummary/UkRiskSummary";
import EURiskSummary from "../EURiskSummary";
import AboutCoin from "../AboutCoin/AboutCoin";
import type { PricesPageProps } from "types/prices";

import { useTranslations } from "i18n";
import { handlePricePageData } from "utils/coinPrices";
import { FILES } from "i18n/constants";
import { useCountryStateCookies } from "hooks/cookies/useCountryStateCookies";
import TradeForm from "../TradeForm/TradeForm";
import DownloadGeminiMobileApp from "./DownloadGeminiMobileApp";
import PriceCard from "../PriceCard";
import LongReferralCard from "../ReferralCard/LongReferralCard";
import ReferralCard from "../ReferralCard";
import SmallPriceCard from "../PriceCard/SmallPriceCard";

export const PricePageContent = (props: PricesPageProps) => {
  const {
    coinPosts,
    coinArticles,
    initialOverview,
    currentCoinDetail,
    referralCardEntry,
    trendingCoins
  } = props;

  const { isUK, isEU, isUS } = useCountryStateCookies();

  const {
    coinResources,
    faqAccordion,
    cryptopediaSmallCard,
    priceBlogs,
    priceArticles,
    aboutCoin
  } = handlePricePageData(props);

  const t = useTranslations(FILES.PRICES);

  const coinSymbolName = get(currentCoinDetail, "coin");
  const coinName = get(currentCoinDetail, "name");
  const isStableCoin = get(currentCoinDetail, "isStableCoin");
  const isMemeCoin = get(currentCoinDetail, "isMemeCoin");
  const isWrappedCoin = get(currentCoinDetail, "isWrappedCoin");
  const isAssetRiskSummaryRequired =
    isStableCoin || isMemeCoin || isWrappedCoin;

  const isTrading = get(currentCoinDetail, "isTrading");
  const isCustody = get(currentCoinDetail, "isCustody");

  const coinType = getCoinType(currentCoinDetail);
  return (
    <React.Fragment>
      {!isTrading && isCustody && (
        <div className="hidden lg:block">
          <SectionMessage
            heading={
              <Body size="md" bold>
                {coinSymbolName} {t("delistedTokenTitle")}
              </Body>
            }
            statusType="info"
            mb={2}
          >
            <Body size="sm">
              {t("delistedTokenMessage", { coinSymbol: coinSymbolName })}
            </Body>
          </SectionMessage>
        </div>
      )}
      <CoinPriceChart
        initialOverview={initialOverview}
        currentCoinDetail={currentCoinDetail}
      />
      <div className="block [@media(min-width:600px)]:px-[16px] [@media(min-width:710px)]:hidden">
        <AboutCoin
          aboutCoin={aboutCoin}
          contentUk={isUK ? currentCoinDetail.contentExtraUk : undefined}
        />
      </div>

      {/* Tablet/Mobile Price Page Content only */}
      <div className="[@media(min-width:599px)]:px-[16px] [@media(min-width:710px)]:px-[48px] [@media(min-width:904px)]:px-0">
        <div className="flex items-center gap-[46px] [@media(min-width:904px)]:hidden">
          <div className="tablet:mt-0 w-full basis-full [@media(min-width:710px)]:basis-2/3">
            <TradeForm
              tradeForm={handlePricePageData(props).tradeForm}
              {...props}
            />
          </div>
          <div className="hidden basis-1/3 [@media(min-width:710px)]:flex">
            <DownloadGeminiMobileApp />
          </div>
        </div>

        {/* Mobile Price Page Content only*/}
        <div className="block [@media(min-width:710px)]:hidden">
          <ReferralCard referralCardEntry={referralCardEntry} />
          <div className="my-[48px]">
            <TokenCardSlider
              title={t("trendingTitle")}
              coinsArray={trendingCoins}
            />
          </div>
          {isUS && (
            <SmallPriceCard
              title={t("theGeminiCreditCard")}
              description={t("earnUpTo")}
              CTALabel={t("applyNow")}
              imageUrl="7aHcNjNuy0uCNivjOuN7MX/3bdd90096a79f80f3dbb64c0a89f651b/Gemini_CC_Bitcoin_Front_3x.png"
              imageContainerClasses="flex justify-center items-center py-[24px]"
              imageClasses="w-[260px] object-contain"
              bgStyles={{
                backgroundImage:
                  "linear-gradient(180deg, #FF7902 0%, #FF6702 54.81%)"
              }}
            />
          )}
          {!isUS && (
            <SmallPriceCard
              title={t("geminiActiveTrader")}
              description={t("tradeBitcoinOnThePlatform", { name: coinName })}
              CTALabel={t("tradeNow")}
              imageUrl="5uQilO6k5u9cAMG2rMkvZD/83c5d3f0eb2323a71510988d469fff38/Group_2147214072.png"
              imageContainerClasses="flex justify-end items-end self-end pt-[24px]"
              imageClasses="w-[310px] object-contain"
              bgStyles={{
                backgroundColor: "#000000"
              }}
            />
          )}
        </div>

        {/* Tablet Price Page Content only */}
        {(isUK || isEU) && isAssetRiskSummaryRequired && coinType && (
          <AssetRiskSummary currentCoinDetail={currentCoinDetail} />
        )}
        {isUK && <UkRiskSummary />}
        {isEU && <EURiskSummary />}
        <LongReferralCard referralCardEntry={referralCardEntry} />
        <div className="hidden [@media(min-width:710px)]:block [@media(min-width:905px)]:hidden">
          <TokenCardSlider
            title={t("trendingTitle")}
            coinsArray={trendingCoins}
          />
        </div>
        <div className="hidden [@media(min-width:710px)]:block">
          <AboutCoin
            aboutCoin={aboutCoin}
            contentUk={isUK ? currentCoinDetail.contentExtraUk : undefined}
          />
        </div>
        <div className="flex flex-col">
          {isUS && (
            <PriceCard
              title={t("theGeminiCreditCard")}
              description={t("earnUpTo")}
              CTALabel={t("applyNow")}
              href="https://www.gemini.com/credit-card"
              bgStyles={{
                backgroundImage: `url('${buildContentfulImageUrl("7aHcNjNuy0uCNivjOuN7MX/3bdd90096a79f80f3dbb64c0a89f651b/Gemini_CC_Bitcoin_Front_3x.png")}')`,
                backgroundSize: "40%",
                backgroundPosition: "90% 50%",
                backgroundRepeat: "no-repeat",
                backgroundColor: "#FF6702"
              }}
            />
          )}
          {!isUS && (
            <PriceCard
              title={t("geminiActiveTrader")}
              href="https://exchange.gemini.com/trade/BTCUSD"
              description={t("tradeBitcoinOnThePlatform", { name: coinName })}
              CTALabel={t("tradeNow")}
              buttonBgColor="#F9A631"
              bgStyles={{
                backgroundImage: `url('${buildContentfulImageUrl("5uQilO6k5u9cAMG2rMkvZD/83c5d3f0eb2323a71510988d469fff38/Group_2147214072.png")}')`,
                backgroundSize: "52%",
                backgroundPosition: "100% 100%",
                backgroundRepeat: "no-repeat",
                backgroundColor: "black"
              }}
            />
          )}
        </div>
        <CoinPosts
          coinPosts={{
            ...priceBlogs,
            title: t("blogTitle", { coinName: priceBlogs?.coinFullName }),
            data: coinPosts
          }}
        />
        <CoinPosts
          coinPosts={{
            ...priceArticles,
            title: t("cryptopediaArticlesTitle", {
              coinName: priceArticles?.coinFullName
            }),
            articles: coinArticles
          }}
        />
        <CoinResources coinResources={coinResources} />
        <FaqAccordion accordion={faqAccordion} />
        {cryptopediaSmallCard.isDisplay && (
          <SmallCard smallCard={cryptopediaSmallCard} className="cryptopedia" />
        )}
        <RecentCoin initialOverview={initialOverview} />
      </div>
    </React.Fragment>
  );
};
